import { useCallback, useRef } from 'react';
import { Misc } from '@/constants';
import { CommonService } from '@/services';
import commonService from '@/services/common.service.ts';

interface RequirementGatheringModeOptions {
    projectId: string;
    message: string;
    previewUrl: string;
    agentType: 'analyst' | 'pm';
    workflowInitialized: boolean;
    sessionId?: string;
    onMessage: (message: any, isStreaming?: boolean) => void;
    onError: (errorMessage?: string) => void;
    onComplete: () => void;
    onContainerWakeStart?: () => void;
    onContainerWakeSuccess?: (newUrl: string) => void;
    waitForPreviewUrl?: () => Promise<string>;
}

// Check if the container is active by making a health check request
const checkContainerStatus = async (previewUrl: string): Promise<boolean> => {
    if (!previewUrl) return false;

    try {
        const url = new URL(previewUrl);
        const newHost = url.host.replace('3000', '7000');
        const healthCheckUrl = `${url.protocol}//${newHost}/health`;

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const response = await fetch(healthCheckUrl, {
            method: 'GET',
            signal: controller.signal
        });

        clearTimeout(timeoutId);
        return response.ok;
    } catch (error) {
        console.log('Container status check failed:', error);
        return false;
    }
};

export const useRequirementGatheringMode = () => {
    // Track active abort controller to allow cancellation
    const activeAbortControllerRef = useRef<AbortController | null>(null);

    const abort = useCallback(() => {
        if (activeAbortControllerRef.current) {
            activeAbortControllerRef.current.abort();
            activeAbortControllerRef.current = null;
        }
    }, []);

    const executeRequirementGathering = useCallback(async (options: RequirementGatheringModeOptions) => {
        const {
            projectId,
            message,
            previewUrl,
            agentType,
            workflowInitialized,
            sessionId: providedSessionId,
            onMessage,
            onError,
            onComplete,
            onContainerWakeStart,
            onContainerWakeSuccess,
            waitForPreviewUrl
        } = options;

        // Abort any existing request before starting a new one
        if (activeAbortControllerRef.current) {
            activeAbortControllerRef.current.abort();
        }

        const MAX_RETRIES = 5; // Increased from 3 to 5
        let retryCount = 0;
        let currentSessionId = providedSessionId;

        let isCompleted = false;
        let isAborted = false;
        let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
        let activityChecker: NodeJS.Timeout | null = null;

        let finalResult = '';
        let streamingText = '';

        let activePreviewUrl = previewUrl;
        const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

        while (retryCount <= MAX_RETRIES && !isCompleted && !isAborted) {
            try {
                const jwtToken = localStorage.getItem(Misc.LOCAL_STORAGE_JWT_TOKEN);
                if (!jwtToken) {
                    onError('Authentication token not found');
                    return;
                }

                activePreviewUrl = previewUrl;

                // Container wake logic (similar to advanced mode)
                if (!previewUrl) {
                    if (retryCount === 0) {
                        onContainerWakeStart?.();
                        commonService._project_service.revertToASession(projectId, {});

                        if (waitForPreviewUrl) {
                            try {
                                activePreviewUrl = await waitForPreviewUrl();
                                onContainerWakeSuccess?.(activePreviewUrl);
                            } catch {
                                onError('Failed to start container. Please try again.');
                                return;
                            }
                        } else {
                            onError('Preview URL not available');
                            return;
                        }
                    }
                } else {
                    const isActive = await checkContainerStatus(previewUrl);

                    if (!isActive) {
                        onContainerWakeStart?.();
                        commonService._project_service.revertToASession(projectId, {});

                        if (waitForPreviewUrl) {
                            try {
                                activePreviewUrl = await waitForPreviewUrl();
                                onContainerWakeSuccess?.(activePreviewUrl);
                            } catch {
                                throw new Error('Failed to wake up container during reconnect.');
                            }
                        }
                    }
                }

                // Extract preview server URL
                let previewServerUrl: string;
                try {
                    const url = new URL(activePreviewUrl);
                    const newHost = url.host.replace('3000', '7000');
                    previewServerUrl = `${url.protocol}//${newHost}/execute`;
                } catch (urlError) {
                    console.error('Error parsing preview URL:', urlError);
                    onError('Invalid preview URL');
                    return;
                }

                // Prepare request with agent context
                const agentName = agentType === 'analyst' ? 'Business Analyst' : 'Product Manager';
                const workflowContext = workflowInitialized
                    ? `\n\nWorkflow initialized. Using structured planning approach.`
                    : '';

                const contextualizedMessage = `As a ${agentName}, help with the following requirement gathering:${workflowContext}\n\n${message}`;

                const requestBody: any = {
                    message: contextualizedMessage,
                    cwd: "/app/apps/current_session",
                    projectId: projectId,
                };

                if (currentSessionId) {
                    requestBody.sessionId = currentSessionId;
                }

                const abortController = new AbortController();
                activeAbortControllerRef.current = abortController;

                const timeoutId = setTimeout(() => {
                    if (abortController) {
                        abortController.abort();
                    }
                }, 1500000); // 25 minutes

                const response = await fetch(previewServerUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${jwtToken}`
                    },
                    body: JSON.stringify(requestBody),
                    signal: abortController.signal,
                });

                clearTimeout(timeoutId);

                if (!response.ok) {
                    if (response.status >= 500 || response.status === 429) {
                        throw new Error(`Server error: ${response.status}`);
                    }

                    try {
                        const errorData = await response.json();
                        onError(errorData?.error || errorData?.message || response.statusText);
                    } catch {
                        onError(response?.statusText || 'Error reading response stream');
                    }
                    return;
                }

                reader = response.body?.getReader();
                if (!reader) {
                    throw new Error('Failed to read response stream');
                }

                const decoder = new TextDecoder();
                let buffer = '';
                let pendingEventType: string | null = null;
                let lastActivityTime = Date.now();
                const ACTIVITY_TIMEOUT = 300000; // 5 minutes

                // Activity checker logic
                if (activityChecker) clearInterval(activityChecker);
                activityChecker = setInterval(() => {
                    const timeSinceLastActivity = Date.now() - lastActivityTime;
                    if (timeSinceLastActivity > ACTIVITY_TIMEOUT) {
                        console.warn('No activity detected, connection may be stale');
                        if (abortController) abortController.abort();
                    }
                }, 10000);

                try {
                    while (true) {
                        const { done, value } = await reader.read();

                        if (done) {
                            console.log('Stream completed successfully');
                            isCompleted = true;
                            break;
                        }

                        lastActivityTime = Date.now();
                        const chunk = decoder.decode(value, { stream: true });
                        buffer += chunk;

                        let newlineIndex;
                        while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
                            const line = buffer.slice(0, newlineIndex);
                            buffer = buffer.slice(newlineIndex + 1);
                            const trimmedLine = line.trim();

                            if (trimmedLine.startsWith(':')) {
                                continue;
                            }

                            if (trimmedLine.startsWith('event:')) {
                                pendingEventType = trimmedLine.replace(/^event:\s*/, '').trim();
                            } else if (trimmedLine.startsWith('data:')) {
                                const jsonStr = trimmedLine.replace(/^data:\s*/, '');
                                if (!jsonStr) continue;

                                try {
                                    const data = JSON.parse(jsonStr);
                                    const eventType = pendingEventType || 'message';
                                    pendingEventType = null;

                                    switch (eventType) {
                                        case 'session':
                                            currentSessionId = data?.sessionId || data?.session_id || null;
                                            if (projectId) {
                                                const currentData = Misc.getSessionData(projectId);
                                                Misc.setSessionData(projectId, {
                                                    ...currentData,
                                                    sessionId: currentSessionId ?? null
                                                });
                                            }
                                            break;

                                        case 'message': {
                                            const messageData = data;
                                            const fullMessage = messageData?.fullMessage;

                                            if (messageData?.type === 'assistant') {
                                                const content = fullMessage?.message?.content || [];

                                                if (Array.isArray(content)) {
                                                    content.forEach((item: any) => {
                                                        if (item.type === 'text' && item.text) {
                                                            streamingText += item.text;
                                                            onMessage({
                                                                id: Date.now(),
                                                                role: 'agent',
                                                                message: streamingText,
                                                                extra: {
                                                                    session_id: currentSessionId,
                                                                    is_streaming: true,
                                                                    agent_type: agentType,
                                                                    workflow_initialized: workflowInitialized
                                                                }
                                                            }, true);
                                                        }
                                                    });
                                                }
                                            } else if (messageData?.type === 'result') {
                                                finalResult = messageData?.result || '';
                                            }
                                            break;
                                        }

                                        case 'complete':
                                            if (data?.result) {
                                                const resultData = data.result;
                                                if (!finalResult && resultData?.result) {
                                                    finalResult = resultData.result;
                                                }

                                                if (finalResult) {
                                                    onMessage({
                                                        id: Date.now(),
                                                        role: 'agent',
                                                        message: finalResult,
                                                        extra: {
                                                            session_id: currentSessionId,
                                                            is_streaming: false,
                                                            is_completed: true,
                                                            agent_type: agentType,
                                                            workflow_initialized: workflowInitialized
                                                        }
                                                    }, false);
                                                }

                                                isCompleted = true;
                                                if (abortController) {
                                                    abortController.abort();
                                                }
                                            }
                                            break;

                                        case 'error':
                                            console.error('Server error:', data);
                                            onError(data?.error || 'Failed to execute requirement gathering');
                                            isCompleted = true;
                                            return;

                                        default:
                                            break;
                                    }
                                } catch (parseError) {
                                    console.error('Error parsing SSE data:', parseError);
                                }
                            }

                            if (isCompleted) break;
                        }

                        if (isCompleted) break;
                    }

                } catch (streamError: unknown) {
                    if (isCompleted || (streamError instanceof Error && streamError.name === 'AbortError')) {
                        console.log('Stream completed or connection aborted intentionally');
                    } else {
                        console.error('Error reading SSE stream:', streamError);
                        throw streamError;
                    }
                } finally {
                    if (reader) {
                        try {
                            reader.releaseLock();
                        } catch (e) {
                            console.warn('Error releasing reader lock:', e);
                        }
                    }
                    if (activityChecker) {
                        clearInterval(activityChecker);
                        activityChecker = null;
                    }
                }

            } catch (error: any) {
                if (error.name === 'AbortError' && isCompleted) {
                    // Success case
                } else {
                    console.warn(`Attempt ${retryCount + 1} failed:`, error);

                    if (retryCount < MAX_RETRIES && !isAborted) {
                        const backoffTime = 1000 * Math.pow(2, retryCount);
                        retryCount++;
                        await delay(backoffTime);
                    } else {
                        console.error('Max retries reached or aborted. Giving up.');
                        if (error.name === 'AbortError') {
                            if (!isCompleted) onError('Request timed out. Please try again.');
                        } else if (error.message?.includes('Failed to fetch')) {
                            onError('Network error. Max retries reached.');
                        } else {
                            onError(error?.message || 'Failed to execute requirement gathering');
                        }
                        isAborted = true;
                    }
                }
            }
        }

        if (isCompleted) {
            commonService._communications.setProjectUrlSubject.next(activePreviewUrl);
            CommonService._communications.fetchProjectCode.next(true);
            onComplete();
        }

        activeAbortControllerRef.current = null;
    }, []);

    return {
        executeRequirementGathering,
        abort
    };
};

